import type { OutputLanguage } from "@/types/aicheck";

export type ProjectStage =
  | "inquiry"
  | "proposal"
  | "quotation"
  | "booking"
  | "final"
  | "post";

export const PROJECT_STAGES: ProjectStage[] = [
  "inquiry",
  "proposal",
  "quotation",
  "booking",
  "final",
  "post",
];

export const PROJECT_STAGE_LABELS: Record<ProjectStage, string> = {
  inquiry: "引合",
  proposal: "提案",
  quotation: "見積",
  booking: "手配",
  final: "直前確認",
  post: "催行後",
};

export type WatchState = "active" | "paused" | "closed";

export const WATCH_STATE_LABELS: Record<WatchState, string> = {
  active: "ウォッチ中",
  paused: "一時停止",
  closed: "終了",
};

export type RunTrigger =
  | "manual"
  | "watch"
  | "date"
  | "stage"
  | "rerun"
  /** フリーフォームレビュー: 履歴に残るが diff 連鎖・通知・アクション起票からは除外 */
  | "freeform";

/** crm = CRM project (namespaced by auth environment), external = uploaded data */
export type ProjectSourceKind = "crm" | "external";

export interface ProjectRecord {
  /** Namespaced identity: `crm:<env>:<id>` or `ext:<digest>` */
  projectKey: string;
  /** Human-facing id (CRM project id, or digest-derived id for external) */
  displayId: string;
  /**
   * 外部案件の公開ID（`ext_...`）。CRM 案件では null。projectKey から導出する
   * 保存しない値で、画面表示と他 API への指定に使う（→ lib/aicheck/external-ref）。
   */
  externalRef: string | null;
  env: string | null;
  sourceKind: ProjectSourceKind;
  sourceLabel: string | null;
  title: string;
  stage: ProjectStage;
  departureDate: string | null;
  watchState: WatchState;
  lastRunId: string | null;
  lastRunAt: string | null;
  /** ia-ui: 最新スナップショット digest（未実行の登録済み案件でも設定される） */
  lastInputDigest: string | null;
  lastRunLanguage: OutputLanguage | null;
  lastOverallScore: number | null;
  lastRunStatus: string | null;
  openFindingCount: number;
  openActionCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * ①ヘルスひとこと（表示専用・非永続）。一覧 API がローカル LLM キャッシュから
   * 付与する。DB には保存しない。未生成 / ローカル不在なら null / undefined。
   */
  healthGist?: string | null;
}

export interface RunRecord {
  runId: string;
  projectKey: string;
  language: OutputLanguage;
  stage: ProjectStage | null;
  trigger: RunTrigger;
  /** ia-eval3: 実行時の設定 / 知識状態ダイジェスト（変更帰属用） */
  configDigest: string | null;
  knowledgeDigest: string | null;
  /**
   * running: 実行中（開始時に記録）。interrupted: プロセス再起動（再デプロイ等）で
   * 完了記録のないまま中断された run（起動時 reconcile で付与）。
   */
  status: "completed" | "error" | "cancelled" | "running" | "interrupted";
  overallScore: number | null;
  startedAt: string;
  completedAt: string | null;
  executedBy: string | null;
  inputDigest: string | null;
  totalTokens: number;
  findingCount: number;
  newCount: number;
  continuedCount: number;
  resolvedCount: number;
}

export interface RunCheckRecord {
  runId: string;
  checkId: string;
  checkName: string;
  status: string;
  score: number | null;
  summary: string | null;
  durationMs: number | null;
  tokensTotal: number;
}

export type FindingSource = "ai" | "consistency";

export interface RunFindingRecord {
  findingId: string;
  runId: string;
  projectKey: string;
  checkId: string;
  source: FindingSource;
  severity: string;
  checkPoint: string;
  finding: string;
  recommendation: string | null;
  evidence: string | null;
  dayNumber: number | null;
  aspectIds: string[];
  factIds: string[];
  fingerprint: string;
  firstSeenRunId: string;
}

export interface RunDiff {
  newFindings: RunFindingRecord[];
  continuedFindings: RunFindingRecord[];
  resolvedFindings: RunFindingRecord[];
  previousRunId: string | null;
}

// ─── Suggested actions ────────────────────────────────────────────────────────

/** confirm = 確認促し, fix = 修正提案, info = 参考情報 */
export type ActionKind = "confirm" | "fix" | "info";

export type ActionStatus =
  | "open"
  | "adopted"
  | "adopted_modified"
  | "dismissed"
  | "expired";

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  open: "未処理",
  adopted: "採用",
  adopted_modified: "修正して採用",
  dismissed: "棄却",
  expired: "解消済み",
};

export const ACTION_KIND_LABELS: Record<ActionKind, string> = {
  confirm: "確認",
  fix: "修正",
  info: "参考",
};

export interface ActionRecord {
  actionId: string;
  projectKey: string;
  /** Run that last produced/updated this action */
  runId: string;
  findingId: string | null;
  fingerprint: string;
  stage: ProjectStage | null;
  kind: ActionKind;
  severity: string;
  checkId: string;
  title: string;
  description: string;
  aspectIds: string[];
  factIds: string[];
  status: ActionStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  dismissReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectEventRecord {
  eventId: string;
  projectKey: string;
  kind: "stage_change" | "case_registered";
  fromStage: ProjectStage | null;
  toStage: ProjectStage | null;
  actor: string | null;
  createdAt: string;
}
